import { useState, useEffect } from "react";
import { useApp } from "context";
import { Custom, Modal } from "components";
import { Enums, Constants, Helpers, Store } from "utils";
import { ButtonGroup, Grid, IconButton } from "@mui/material";
import { PairValue } from "types";
import { IAppointment, IEvent, IService } from "interfaces";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import NextPlanIcon from "@mui/icons-material/NextPlan";

type Props = {
  opened: boolean;
  toggle: () => void;
  selectedHealth360s?: IAppointment[];
  updateSelectedHealth360s?: (health360s: IAppointment[]) => void;
};

type Upcoming = "upcoming";
const upcoming = "upcoming";

type All = "all";
const all = "all";

export const Health360 = (props: Props) => {
  const { t, theme, service: providedService, locale } = useApp();

  const [appointments, setAppointments] = useState<IAppointment[]>([]);
  const [providers, setProviders] = useState<PairValue[]>([]);
  const [services, setServices] = useState<PairValue[]>([]);
  const [providerEvents, setProviderEvents] = useState<IEvent[]>([]);
  const [health360VirtualEvents, setHealth360VirtualEvents] = useState<
    IEvent[]
  >([]);
  const [notes, setNotes] = useState("");
  const [editNotesModalOpened, setEditNotesModalOpened] = useState(false);
  const [health360ModalOpened, setHealth360ModalOpened] = useState(false);
  const [viewPdfModalOpened, setViewPdfModalOpened] = useState(false);
  const [healthPlanModalOpened, setHealthPlanModalOpened] = useState(false);
  const [healthPlanNotes, setHealthPlanNotes] = useState("");
  const [appointment, setAppointment] = useState<IAppointment | null>(null);
  const [period, setPeriod] = useState<Enums.EnumPeriod | Upcoming | All>(
    Enums.EnumPeriod.Day
  );
  const [incrementComponent, setIncrementComponent] = useState(0);

  const fetchServices = () => {
    providedService.service
      .all()
      .then((res) =>
        setServices(
          res
            .map((s: IService) => ({ key: s.serviceId, value: s.serviceName }))
            .sort((a: PairValue, b: PairValue) => b.key - a.key)
        )
      );
  };

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    fetchProviders();
  }, [incrementComponent]);

  const fetchProviders = async () => {
    // TODO: clinicId (QUBE Medical) and serviceId (Health 360) are hardcoded
    const providers = await providedService.provider.findByClinicAndService(
      3,
      12
    );

    setProviders([{ key: -1, value: "-" }, ...providers]);

    const { providerEvents, clinicEvents } =
      await providedService.calendar.find();

    setProviderEvents(providerEvents);
    setHealth360VirtualEvents(
      clinicEvents.filter((e: IEvent) => e.appointmentId)
    );
  };

  useEffect(() => {
    findAndSetHealth360Appointments(period);
  }, [period, incrementComponent]);

  useEffect(() => {
    handleSelectHealth360s(
      (props.selectedHealth360s ?? []).map((app) => app.appointmentId)
    );
  }, [appointments]);

  const handleSelectHealth360s = (appIds: number[]) => {
    if (props.updateSelectedHealth360s) {
      const arr = appointments.filter((app) =>
        appIds.includes(app.appointmentId)
      );

      arr.forEach((app) => {
        app.providerName = providers.find(
          (pr) => pr.key === app.providerId
        )?.value;
      });

      props.updateSelectedHealth360s(arr);
    }
  };

  const isClinic = Store.sessionStorage.isClinic();
  const userId = Store.sessionStorage.get().userId;

  // TODO: there should be a route to do the filter and sort from DB for appointments. lazy programming.
  const findAndSetHealth360Appointments = async (
    period: Enums.EnumPeriod | Upcoming | All = Enums.EnumPeriod.Day
  ) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const appointments =
      period === all
        ? (await providedService.appointment.all()).appointments
        : period === upcoming
        ? (await providedService.appointment.all()).appointments.filter(
            (appointment: IAppointment) =>
              new Date(appointment.scheduledTime) >= today
          )
        : (await providedService.appointment.period(period)).appointments;

    const [status, health360s] = await providedService.health360.all();

    const allAppointments = appointments
      .filter(
        (app: IAppointment) =>
          // app.serviceId === 12 &&
          isClinic || app.providerId === userId
      )
      .map((app: IAppointment) => ({
        ...app,
        // TODO: removing Aalok Shah which is the default provider set for apps coming from wix
        ...(app.providerId === 5 && { providerId: null }),
        health360: health360s.find(
          (item: any) => item.appointmentId === app.appointmentId
        ),
      }))
      .sort((a: IAppointment, b: IAppointment) =>
        b.scheduledTime.localeCompare(a.scheduledTime)
      );

    setAppointments(allAppointments);
  };

  const action = (
    <ButtonGroup>
      {[
        { label: t.relative_date.today, period: Enums.EnumPeriod.Day },
        { label: t.relative_date.last_week, period: Enums.EnumPeriod.Week },
        { label: t.relative_date.last_month, period: Enums.EnumPeriod.Month },
        { label: "Upcoming", period: upcoming },
        { label: "All", period: all },
      ].map((item, index) => (
        <Custom.Button
          key={index}
          size="small"
          variant="contained"
          onClick={() => setPeriod(item.period as any)}
          sx={{ marginBottom: theme.margin.sm }}
        >
          {item.label}
        </Custom.Button>
      ))}
    </ButtonGroup>
  );

  const columns = [
    {
      field: "username",
      width: 200,
      headerName: t.label.name,
      renderCell: ({ row }: { row: IAppointment }) =>
        Helpers.replaceComma(row.username),
    },
    {
      field: "scheduledTime",
      width: 200,
      headerName: "Scheduled Date/Time",
      renderCell: ({ row }: { row: IAppointment }) =>
        new Date(row.scheduledTime).toLocaleTimeString(
          locale,
          Constants.DATE_FORMAT
        ),
    },
    {
      field: "reason",
      width: 200,
      sortable: false,
      headerName: t.label.reason,
    },
    {
      field: "provider",
      width: 200,
      sortable: false,
      headerName: "Provider",
      renderCell: ({ row }: { row: IAppointment }) => {
        return isClinic ? (
          <Custom.Select
            items={providers}
            value={row.providerId ?? ""}
            onChange={(providerId: number) =>
              handleProviderChange(row, providerId)
            }
          />
        ) : (
          providers.find((pr) => pr.key === row.providerId)?.value ?? ""
        );
      },
    },
    {
      field: "service",
      width: 200,
      sortable: false,
      headerName: "Service",
      renderCell: ({ row }: { row: IAppointment }) => {
        return isClinic ? (
          <Custom.Select
            items={services}
            value={row.serviceId ?? ""}
            onChange={(serviceId: number) =>
              handleServiceChange(row, serviceId)
            }
          />
        ) : (
          services.find((sr) => sr.key === row.serviceId)?.value ?? ""
        );
      },
    },
    {
      field: "appointmentNotes",
      width: 300,
      sortable: false,
      headerName: "Notes",
      renderCell: ({ row }: { row: IAppointment }) => (
        <div
          onClick={() => onEditNotesClick(row)}
          style={{
            width: "280px",
            height: "100%",
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row?.appointmentNotes ?? ""}
          </div>
        </div>
      ),
    },
    {
      field: "actions",
      width: 200,
      sortable: false,
      headerName: t.label.actions,
      renderCell: ({ row }: { row: IAppointment }) =>
        row.serviceId === 12 ? (
          <>
            <IconButton onClick={() => onEditClick(row)}>
              <EditIcon />
            </IconButton>
            <IconButton onClick={() => onViewPdfClick(row)}>
              <PictureAsPdfIcon />
            </IconButton>
            <IconButton onClick={() => onHealthPlanClick(row)}>
              <NextPlanIcon />
            </IconButton>
          </>
        ) : null,
    },
  ];

  const onRowClick = (row: IAppointment) => {
    const appointment =
      appointments.find(
        (app: IAppointment) => app.appointmentId === row.appointmentId
      ) ?? null;
    setAppointment(appointment);
    return appointment;
  };

  const onEditNotesClick = (row: IAppointment) => {
    const appointment = onRowClick(row);
    setNotes(appointment?.appointmentNotes ?? "");
    setEditNotesModalOpened(true);
  };

  const handleCloseEditNotesModal = () => {
    setNotes("");
    setEditNotesModalOpened(false);
    setAppointment(null);
    setIncrementComponent(incrementComponent + 1);
  };

  const onEditClick = (row: IAppointment) => {
    onRowClick(row);
    setHealth360ModalOpened(true);
  };

  const handleCloseHealth360Modal = () => {
    setHealth360ModalOpened(false);
    setAppointment(null);
    setIncrementComponent(incrementComponent + 1);
  };

  const onViewPdfClick = (row: IAppointment) => {
    onRowClick(row);
    setViewPdfModalOpened(true);
  };

  const handleCloseViewPdfModal = () => {
    setViewPdfModalOpened(false);
    setAppointment(null);
    setIncrementComponent(incrementComponent + 1);
  };

  const onHealthPlanClick = (row: IAppointment) => {
    const appointment = onRowClick(row);
    setHealthPlanNotes(appointment?.health360?.healthPlan ?? "");
    setHealthPlanModalOpened(true);
  };

  const handleCloseHealthPlanModal = () => {
    setHealthPlanNotes("");
    setHealthPlanModalOpened(false);
    setAppointment(null);
    setIncrementComponent(incrementComponent + 1);
  };

  const handleProviderChange = async (
    appointment: IAppointment,
    providerId: number
  ) => {
    await providedService.appointment.updateProvider(
      appointment.appointmentId,
      providerId,
      appointment.patientId
    );

    setIncrementComponent(incrementComponent + 1);
  };

  const handleServiceChange = async (
    appointment: IAppointment,
    serviceId: number
  ) => {
    await providedService.appointment.updateService(
      appointment.appointmentId,
      serviceId,
      appointment.patientId
    );

    setIncrementComponent(incrementComponent + 1);
  };

  const submitNotes = async () => {
    const [status, response] = await providedService.appointment.updateNotes(
      appointment?.appointmentId ?? NaN,
      notes,
      appointment?.patientId
    );

    if (status === Enums.EnumResponse.Success) handleCloseEditNotesModal();
  };

  const submitHealthPlanNotes = async () => {
    const [status, response] = await providedService.health360.upsert(
      appointment?.appointmentId ?? NaN,
      {
        healthPlan: healthPlanNotes,
        ...(!isClinic && { healthPlanConfirmed: true }),
      }
    );

    if (status === Enums.EnumResponse.Success) handleCloseHealthPlanModal();
  };

  return (
    <Custom.Card
      title={"QUBE Health"}
      action={action}
      actionSx={{
        float: "initial",
        marginTop: "1rem",
      }}
      opened={props.opened}
      toggle={props.toggle}
    >
      <Grid container spacing={theme.margin.md}>
        <Grid item xs={12} md={12}>
          <Custom.DataGrid
            pageSize={25}
            columns={columns}
            rows={appointments}
            isLoading={false}
            quickSearch
            getRowId={(row: IAppointment) => row.appointmentId}
            {...(isClinic && { onSelect: handleSelectHealth360s })}
          ></Custom.DataGrid>
        </Grid>
      </Grid>
      <Modal.Health360
        appointment={appointment}
        providers={providers}
        providerEvents={providerEvents}
        health360VirtualEvent={
          health360VirtualEvents.find(
            (e: IEvent) => e.appointmentId === appointment?.appointmentId
          ) ?? null
        }
        opened={health360ModalOpened}
        onClose={handleCloseHealth360Modal}
      />
      <Modal.Health360PDF
        opened={viewPdfModalOpened}
        onClose={handleCloseViewPdfModal}
        appointment={appointment}
      />
      <Custom.Modal
        header={"Health 360 Notes"}
        opened={editNotesModalOpened}
        onClose={handleCloseEditNotesModal}
      >
        <Grid container spacing={theme.margin.sm}>
          <Grid item xs={12} md={6}>
            <Custom.TextField
              label={t.label.name}
              readOnly
              value={Helpers.replaceComma(appointment?.username ?? "")}
            ></Custom.TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <Custom.TextField
              label={t.label.check_in_datetime}
              readOnly
              value={new Date(
                appointment?.checkInTime ?? ""
              ).toLocaleTimeString(locale, Constants.DATE_FORMAT)}
            ></Custom.TextField>
          </Grid>
          <Grid item xs={12} md={12} sx={{ marginTop: "3rem" }}>
            <Custom.TextField
              label={"Notes"}
              multiline
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            ></Custom.TextField>
            <Custom.Button sx={{ marginTop: "0.75rem" }} onClick={submitNotes}>
              Submit
            </Custom.Button>
          </Grid>
        </Grid>
      </Custom.Modal>
      <Custom.Modal
        header={"Health Plan"}
        opened={healthPlanModalOpened}
        onClose={handleCloseHealthPlanModal}
      >
        <Grid container spacing={theme.margin.sm}>
          <Grid item xs={12} md={6}>
            <Custom.TextField
              label={t.label.name}
              readOnly
              value={Helpers.replaceComma(appointment?.username ?? "")}
            ></Custom.TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <Custom.TextField
              label={t.label.check_in_datetime}
              readOnly
              value={new Date(
                appointment?.checkInTime ?? ""
              ).toLocaleTimeString(locale, Constants.DATE_FORMAT)}
            ></Custom.TextField>
          </Grid>
          {isClinic && (
            <Grid item xs={12} md={6} sx={{ marginTop: "3rem" }}>
              <Custom.Chip
                label={
                  appointment?.health360?.healthPlanConfirmed
                    ? "Confirmed"
                    : "Pending"
                }
                sx={{
                  width: 100,
                  backgroundColor: appointment?.health360?.healthPlanConfirmed
                    ? theme.color.status.success.color
                    : theme.color.primary.color,
                  "&:hover": {
                    backgroundColor: appointment?.health360?.healthPlanConfirmed
                      ? theme.color.status.success.color
                      : theme.color.primary.color,
                  },
                }}
              ></Custom.Chip>
            </Grid>
          )}
          <Grid item xs={12} md={12} sx={{ marginTop: "3rem" }}>
            <Custom.TextField
              label={"Health Plan"}
              multiline
              autoHeight
              value={healthPlanNotes}
              onChange={(event) => setHealthPlanNotes(event.target.value)}
            ></Custom.TextField>
            {(isClinic || appointment?.providerId === userId) && (
              <Custom.Button
                sx={{ marginTop: "0.75rem" }}
                onClick={submitHealthPlanNotes}
              >
                {isClinic ? "Submit" : "Confirm"}
              </Custom.Button>
            )}
          </Grid>
        </Grid>
      </Custom.Modal>
    </Custom.Card>
  );
};
